'use client'

import { useMemo, useState } from 'react'
import { cn, DisclosureSlot, Input, Slider } from '@nwwp/ui-shadcn'
import { useVeritonic } from '@nwwp/veritonic'
import { calculateCoastFire } from './_lib/calculateCoastFire'
import { CoastFireChart } from './CoastFireChart'
import { LeadMagnetScheduleCTA } from '../../chrome/LeadMagnetScheduleCTA'
import { ScheduleCallCTA } from '../../controls/ScheduleCallCTA'
import { useClientMounted } from '../../hooks/use-client-mounted'
import { useLeadMagnetUnlocked } from '../../hooks/use-lead-magnet-unlocked'
import type { CreateLeadMagnetLeadFn } from '../../lead-magnets/components/LeadCaptureForm'
import { LeadCaptureForm } from '../../lead-magnets/components/LeadCaptureForm'
import type { LeadMagnetPage } from '../../lead-magnets/config'
import { buildScheduleRedirectUrl, PAID_MEDIA_CALENDLY_URL } from '../../utils/ctas'
import { parseCurrency } from '../../utils/currency-format'
import { loadLeadMagnetIncomeQualification, loadLeadMagnetQualification } from '../../utils/lead-magnet-qualification'

/** In-page unlock form — not a LEAD_MAGNET_PAGES gate. Fields match the retired /coast magnet minus schedule_call_interest. */
const COAST_UNLOCK_MAGNET: LeadMagnetPage = {
  slug: 'coast',
  pageTitle: 'Coast FIRE Calculator',
  headline: 'Coast FIRE Calculator',
  body: null,
  ctaText: 'See my results',
  ctaType: 'reveal',
  disclosure:
    'The calculator is provided for informational and educational purposes only. Results are hypothetical estimates based on the information you provide and should not be construed as investment, tax, or legal advice, or as a guarantee of future results. NWWP is an SEC-registered investment adviser. Registration does not imply a certain level of skill or training. See the General Disclosure.',
  formFields: ['first_name', 'last_name', 'household_income', 'investable_assets_range', 'email', 'phone'],
  orderFieldsAsListed: true,
  hideRequiredAsterisks: true,
  phoneRequired: true,
  submitVariant: 'funnel',
  disclosureClassName: 'text-body-xs',
}

function formatCommas(s: string): string {
  const digits = s.replace(/[^0-9]/g, '')
  if (!digits) return ''
  return parseInt(digits, 10).toLocaleString('en-US')
}

function fmtShort(n: number): string {
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return '$' + (abs / 1_000_000).toFixed(2) + 'M'
  if (abs >= 1_000) return '$' + Math.round(abs / 1_000) + 'K'
  return '$' + Math.round(abs).toLocaleString('en-US')
}

function fmtFull(n: number): string {
  return '$' + Math.round(n).toLocaleString('en-US')
}

function HomeIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  )
}

function GraduationIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
      <path d="M6 12v5c3 3 9 3 12 0v-5" />
    </svg>
  )
}

function ForkIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
      <path d="M7 2v20" />
      <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7" />
    </svg>
  )
}

/** Compact, thin-bordered inputs for the Coast calculator card. */
const COAST_INPUT_CLASS = cn('rounded-lg py-2 text-body-sm font-semibold', 'shadow-[0_0_0_1px_var(--color-smoke)]')

/** Plain gray track with a white, outlined thumb (no filled range). */
const COAST_SLIDER_CLASS = cn(
  '[--slider-thumb-color:var(--color-white)]',
  '[&_[data-slot=slider-range]]:hidden',
  '[&_[data-slot=slider-thumb]]:size-4'
)

/** Post-unlock CTAs skip /schedule_form (the visitor just submitted it) and go straight to Calendly. */
const COAST_CALENDLY_HREF = buildScheduleRedirectUrl(PAID_MEDIA_CALENDLY_URL, {})

const TIPS = [
  {
    title: "📉 Don't overestimate your returns",
    body: "Markets don't move in straight lines. If you're planning to coast, build in a buffer rather than assuming the historical average will carry you. If the math still works at a lower return, you have better margin of safety.",
  },
  {
    title: "💸 Don't let lifestyle creep raise your target",
    body: 'Once you decide you can stop saving for retirement, spending often rises to fill the gap. Remember: A bigger lifestyle means a bigger retirement number. Coast FIRE works when expenses stay roughly stable. The moment your annual spending target rises, so does everything else in this model.',
  },
  {
    title: "🎲 Don't coast on a fragile income",
    body: 'Coasting is not the same as retiring. Coast FIRE assumes you still earn enough to cover your living expenses right up until retirement. So a long layoff, a health event or a big life change could disrupt that. Strong Coast plans include an emergency fund and may even include some continued (even small) investing as a buffer.',
  },
  {
    title: '🎯 Stick to your priorities',
    body: 'Deciding at 40 that you want to retire at 55 instead of 65 changes everything in this model. Coast FIRE locks in your timeline. If your goals shift, your Coast number shifts, too. Think of this less as a one-time calculation and more as something to revisit every few years.',
  },
]

type Props = { createLead: CreateLeadMagnetLeadFn }

export function CoastFireCalculator({ createLead }: Props) {
  const [currentAgeStr, setCurrentAgeStr] = useState('35')
  const [retireAgeStr, setRetireAgeStr] = useState('65')
  const [savingsStr, setSavingsStr] = useState('250,000')
  const [contribStr, setContribStr] = useState('3,000')
  const [annualExpenses, setAnnualExpenses] = useState(100_000)
  const [returnRate, setReturnRate] = useState(7)
  const [isAccOpen, setIsAccOpen] = useState(false)
  const { trackFlowStart } = useVeritonic()
  const mounted = useClientMounted()
  const unlocked = useLeadMagnetUnlocked('coast')
  // Re-read sessionStorage when unlock flips so first-submit qualified users see the CTA.
  const isQualified = useMemo(() => mounted && loadLeadMagnetQualification(), [mounted, unlocked])
  // Sticky banner targets $100K+ household income regardless of investable assets.
  const isIncomeQualified = useMemo(() => mounted && loadLeadMagnetIncomeQualification(), [mounted, unlocked])
  const showStickyBanner = unlocked && isIncomeQualified

  const currentAge = Math.max(18, Math.min(69, parseInt(currentAgeStr) || 35))
  const retireAge = Math.max(currentAge + 1, Math.min(80, parseInt(retireAgeStr) || 65))

  const results = calculateCoastFire({
    currentAge,
    retireAge,
    currentSavings: parseCurrency(savingsStr),
    monthlyContrib: parseCurrency(contribStr),
    annualExpenses,
    returnRate: returnRate / 100,
  })

  const isOnTrack = results.alreadyCoasted || results.coastAge !== null

  const currentYear = new Date().getFullYear()
  const coastYear = results.coastAge !== null ? currentYear + (results.coastAge - currentAge) : null
  const target = annualExpenses * 25
  const overshoot = results.projectedBalance - target
  const annualSurplus = parseCurrency(contribStr) * 12

  let heroEyebrow: string
  let heroAge: string
  let heroYear: string
  let yearsLabel: string
  let yearsCount: string
  let yearsDesc: string

  if (results.alreadyCoasted) {
    heroEyebrow = 'Coast FIRE status'
    heroAge = 'Already there'
    heroYear = 'You crossed the line at or before today'
    yearsLabel = 'Years of compounding ahead'
    yearsCount = String(retireAge - currentAge)
    yearsDesc = 'Your current balance already compounds to your target without another dollar of contributions.'
  } else if (results.coastAge !== null) {
    const yearsAway = results.coastAge - currentAge
    heroEyebrow = "You'll hit Coast FIRE at age"
    heroAge = String(results.coastAge)
    heroYear = `in ${String(coastYear)}`
    yearsLabel = yearsAway === 1 ? 'Year until you can coast' : 'Years until you can coast'
    yearsCount = String(yearsAway)
    yearsDesc = `Keep contributing at your current rate and you will cross the threshold in ${yearsAway} ${yearsAway === 1 ? 'year' : 'years'}.`
  } else {
    heroEyebrow = 'Coast FIRE status'
    heroAge = '–'
    heroYear = 'Not on track to coast before retirement with current inputs'
    yearsLabel = 'Action needed'
    yearsCount = '–'
    yearsDesc =
      'Increase your monthly contributions or reduce your target retirement expenses to reach Coast FIRE before retirement.'
  }

  return (
    <div className="bg-background">
      <div
        className={cn(
          'relative z-10 mx-auto -mt-5 max-w-225 px-4 pb-16 tablet:px-6',
          showStickyBanner && 'pb-36 tablet:pb-28'
        )}
      >
        {/* Accordion */}
        <div className="mb-4 overflow-hidden rounded-2xl border border-border bg-card">
          <button
            type="button"
            className="flex w-full items-center justify-between px-6 py-4 text-left hover:bg-green-darker/2"
            onClick={() => setIsAccOpen((o) => !o)}
            aria-expanded={isAccOpen}
            aria-controls="def-body"
          >
            <div className="flex items-center gap-3">
              <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-green-darker text-body-xs font-bold text-white">
                ?
              </div>
              <span className="text-title-xs text-gray-darker">What is Coast FIRE?</span>
            </div>
            <svg
              className={cn(
                'size-5 shrink-0 text-neutral-darker transition-transform duration-300',
                isAccOpen && 'rotate-180'
              )}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          <div
            id="def-body"
            className={cn('overflow-hidden transition-all duration-300', isAccOpen ? 'max-h-150 pb-5' : 'max-h-0')}
          >
            <div className="flex flex-col gap-3 px-6">
              <p className="text-body-sm text-neutral-darker">
                Coast FIRE (FIRE stands for &quot;financial independence, retire early&quot;) is the point at which your
                savings have gotten big enough that compounding can grow your portfolio to your target retirement number
                — without another dollar of contributions.
              </p>
              <p className="text-body-sm text-neutral-darker">
                Once you hit your Coast FIRE number, you may be able to stop making monthly contributions to your
                retirement accounts and instead let the balance grow on its own to reach your target. Coasting
                isn&apos;t the same as retiring (you&apos;ll still need to earn enough to cover your living expenses),
                but the retirement savings part? Compounding handles it.
              </p>
              <p className="text-body-sm text-neutral-darker">
                The earlier you hit your Coast number, the more freedom you might gain: freedom to take a lower-paying
                job you love, work less, spend more on experiences or simply stop grinding toward a savings goal
                you&apos;ve already met.
              </p>
            </div>
          </div>
        </div>

        {/* Caveat banner */}
        <div className="mb-4 rounded-2xl border border-l-4 border-border border-l-green-lighter bg-card px-5 py-4 text-body-sm text-neutral-darker">
          <strong className="font-semibold text-gray-darker">What this calculator can&apos;t do: </strong> This
          calculator doesn&apos;t account for taxes, market volatility, inflation, Social Security income, employer
          matches, job changes, divorce or early retirement. Projections assume a consistent annual return. Use it for
          directional insight, not a final plan. A financial advisor can build the complete picture.
        </div>

        {/* Inputs */}
        <div className="mb-4 rounded-2xl border border-border bg-card px-7 py-6">
          <h2 className="text-title-xs border-b border-border pb-4 text-gray-darker">Your inputs</h2>

          <div className="my-5 grid grid-cols-1 gap-4 tablet:grid-cols-2">
            <div>
              <label htmlFor="currentAge" className="mb-2 block text-subtitle font-bold text-black">
                Current age
              </label>
              <Input
                type="text"
                inputMode="numeric"
                id="currentAge"
                aria-label="Current age"
                value={currentAgeStr}
                onChange={(e) => setCurrentAgeStr(e.target.value.replace(/[^0-9]/g, ''))}
                onBlur={() => setCurrentAgeStr(String(currentAge))}
                className={COAST_INPUT_CLASS}
              />
            </div>

            <div>
              <label htmlFor="retireAge" className="mb-2 block text-subtitle font-bold text-black">
                Target retirement age
              </label>
              <Input
                type="text"
                inputMode="numeric"
                id="retireAge"
                aria-label="Target retirement age"
                value={retireAgeStr}
                onChange={(e) => setRetireAgeStr(e.target.value.replace(/[^0-9]/g, ''))}
                onBlur={() => setRetireAgeStr(String(retireAge))}
                className={COAST_INPUT_CLASS}
              />
              <p className="mt-3 text-body-xs text-neutral-darker">
                The age you want to stop working — the calculator will show when you can start coasting.
              </p>
            </div>

            <div>
              <label htmlFor="currentSavings" className="mb-2 block text-subtitle font-bold text-black">
                Current invested retirement assets
              </label>
              <Input
                type="text"
                inputMode="numeric"
                id="currentSavings"
                aria-label="Current invested assets in dollars"
                value={savingsStr}
                onChange={(e) => setSavingsStr(formatCommas(e.target.value))}
                prefix="$"
                className={COAST_INPUT_CLASS}
              />
              <p className="mt-3 text-body-xs text-neutral-darker">
                Such as your 401(k), IRAs or brokerage accounts — not your emergency fund.
              </p>
            </div>

            <div>
              <label htmlFor="monthlyContrib" className="mb-2 block text-subtitle font-bold text-black">
                Monthly retirement contribution
              </label>
              <Input
                type="text"
                inputMode="numeric"
                id="monthlyContrib"
                aria-label="Monthly contribution in dollars"
                value={contribStr}
                onChange={(e) => setContribStr(formatCommas(e.target.value))}
                prefix="$"
                className={COAST_INPUT_CLASS}
              />
              <p className="mt-3 text-body-xs text-neutral-darker">
                What you&apos;re currently saving for retirement each month.
              </p>
            </div>
          </div>

          <div className="mb-3">
            <div className="mb-2 flex items-baseline justify-between">
              <span className="text-subtitle font-bold text-black">Expected annual living expenses in retirement</span>
              <span className="text-body-sm font-bold text-green-darker tabular-nums">{fmtFull(annualExpenses)}</span>
            </div>
            <Slider
              className={COAST_SLIDER_CLASS}
              aria-label="Expected annual retirement expenses"
              min={30_000}
              max={500_000}
              step={5_000}
              value={[annualExpenses]}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              onValueChange={(values) => setAnnualExpenses(values[0]!)}
            />
            <p className="mt-3 text-body-xs text-neutral-darker">
              This drives your retirement target (expenses × 25). Think about what you&apos;ll actually spend — housing,
              travel, healthcare, lifestyle — in today&apos;s dollars. Many retirees spend 70%–80% of their
              pre-retirement income.
            </p>
          </div>

          <div className="my-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-bone p-4">
            <div>
              <p className="mb-1 text-body-xs font-bold text-neutral-darker uppercase">
                Your retirement target (expenses × 25)
              </p>
              <p className="text-body-bold-lg font-bold text-gray-darker tabular-nums">{fmtFull(target)}</p>
            </div>
            <p className="max-w-65 text-body-xs text-neutral-darker">Based on a 4% safe withdrawal rate.</p>
          </div>

          <div>
            <div className="mb-2 flex items-baseline justify-between">
              <span className="text-subtitle font-bold text-black">Estimated annual return</span>
              <span className="text-body-sm font-bold text-green-darker tabular-nums">{returnRate.toFixed(1)}%</span>
            </div>
            <Slider
              className={COAST_SLIDER_CLASS}
              aria-label="Expected annual return rate"
              min={3}
              max={12}
              step={0.5}
              value={[returnRate]}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              onValueChange={(values) => setReturnRate(values[0]!)}
            />
            <p className="mt-3 text-body-xs text-neutral-darker">
              Returns vary and depend on how you invest. The S&P 500 has averaged ~10% historically; 7% could account
              for inflation and fees. If you want extra margin of safety, consider 5%–6%.
            </p>
          </div>
        </div>

        {/* Results — not rendered until unlocked (cookie or this session). Inputs stay public for SEO. */}
        {!unlocked ? (
          <div className="mb-4 rounded-2xl border border-border bg-card px-7 py-6">
            <p className="mb-2 text-body-xs font-bold text-green-dark uppercase">Your results are ready</p>
            <h2 className="text-title-sm mb-3 font-semibold text-gray-darker tablet:text-title-xs">
              Unlock your Coast FIRE number
            </h2>
            <p className="mb-6 text-subtitle text-neutral-darker">
              Share a few details to see when you can start coasting and your Coast FIRE number.
            </p>
            <LeadCaptureForm
              magnet={COAST_UNLOCK_MAGNET}
              createLead={createLead}
              onFlowStart={() => {
                try {
                  trackFlowStart()
                } catch (e) {
                  console.warn('trackFlowStart failed', { error: e })
                }
              }}
            />
            <DisclosureSlot className="mt-6 rounded-none border-0 bg-transparent p-0">
              <p className="text-body-xs text-neutral-darker">{COAST_UNLOCK_MAGNET.disclosure}</p>
            </DisclosureSlot>
          </div>
        ) : (
          <>
            {/* Results hero */}
            <div
              className="mb-4 rounded-2xl bg-gray-darker px-5 py-7 shadow-[0_8px_32px_rgba(0,0,0,0.25)] tablet:px-9 tablet:py-8"
              aria-live="polite"
            >
              <div className="flex flex-col tablet:flex-row">
                <div className="mb-5 flex-1 border-b border-white/12 pb-5 tablet:mb-0 tablet:border-r tablet:border-b-0 tablet:pr-8 tablet:pb-0">
                  <p className="mb-3 text-body-xs font-bold text-green uppercase">{heroEyebrow}</p>
                  <p className="text-title-2xl font-bold text-bone-light tabular-nums">{heroAge}</p>
                  <p className="text-body-lg font-semibold text-bone-light/75">{heroYear}</p>
                </div>
                <div className="flex flex-1 flex-col justify-center tablet:pl-8">
                  <p className="mb-3 text-body-xs font-bold text-green uppercase">{yearsLabel}</p>
                  <p className="text-title-xl font-bold text-bone-light tabular-nums">{yearsCount}</p>
                  <p className="text-subtitle text-bone-light/65">{yearsDesc}</p>
                </div>
              </div>
            </div>

            {/* Metrics */}
            <div className="mb-4 rounded-2xl border border-border bg-card px-7 py-6">
              <div className="grid grid-cols-1 gap-px overflow-hidden rounded-xl bg-smoke tablet:grid-cols-3">
                <div className="bg-bone-light p-5">
                  <p className="mb-3 text-body-xs font-bold text-neutral-darker uppercase">Coast FIRE number</p>
                  <p className="text-body-bold-lg font-bold text-gray-darker tabular-nums">
                    {fmtShort(results.coastFireNumber)}
                  </p>
                  <p className="mt-1 text-body-xs text-neutral-darker">amount you need invested today to coast</p>
                </div>
                <div className="bg-bone-light p-5">
                  <p className="mb-3 text-body-xs font-bold text-neutral-darker uppercase">
                    Projected retirement balance
                  </p>
                  <p className="text-body-bold-lg font-bold text-gray-darker tabular-nums">
                    {fmtShort(results.projectedBalance)}
                  </p>
                  <p className={cn('mt-1 text-body-xs font-semibold', overshoot > 0 ? 'text-green' : 'text-red')}>
                    {overshoot > 0 ? `overshoot: +${fmtShort(overshoot)}` : 'below target — save more'}
                  </p>
                </div>
                {isOnTrack ? (
                  <div className="bg-bone-light p-5">
                    <p className="mb-3 text-body-xs font-bold text-neutral-darker uppercase">
                      Monthly surplus once you hit Coast
                    </p>
                    <p className="text-body-bold-lg font-bold text-gray-darker tabular-nums">
                      {fmtFull(results.monthlySurplus)}/mo
                    </p>
                    <p className="mt-1 text-body-xs text-neutral-darker">yours to redirect each month</p>
                  </div>
                ) : (
                  <div className="bg-bone-light p-5">
                    <p className="mb-3 text-body-xs font-bold text-neutral-darker uppercase">
                      Monthly surplus once you hit Coast
                    </p>
                    <p className="text-body-bold-lg font-bold text-red tabular-nums">Not on track</p>
                    <p className="mt-1 text-body-xs text-neutral-darker">
                      increase contributions or reduce expenses to reach Coast FIRE
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Chart */}
            <div className="mb-4 rounded-2xl border border-border bg-card px-7 py-6">
              <div className="mb-4 flex flex-wrap gap-5" aria-hidden="true">
                <span className="flex items-center gap-3 text-body-xs text-neutral-darker">
                  <span className="size-3 shrink-0 rounded-[2px] bg-blue-medium" />
                  Your projected portfolio
                </span>
                <span className="flex items-center gap-3 text-body-xs text-neutral-darker">
                  <span
                    className="size-3 shrink-0 rounded-[2px]"
                    style={{
                      background:
                        'repeating-linear-gradient(90deg, var(--color-green) 0, var(--color-green) 4px, transparent 4px, transparent 8px)',
                    }}
                  />
                  Coast FIRE threshold
                </span>
                <span className="flex items-center gap-3 text-body-xs text-neutral-darker">
                  <span className="size-3 shrink-0 rounded-full bg-orange" />
                  Coast FIRE crossover
                </span>
              </div>
              <CoastFireChart chartData={results.chartData} coastAge={results.coastAge} />
            </div>

            {/* Surplus */}
            {isOnTrack && (
              <div className="mb-8 rounded-2xl border border-border bg-card px-7 py-6">
                <h3 className="text-title-xs mb-4 font-semibold text-gray-darker">
                  After you reach Coast FIRE status, that monthly retirement contribution could instead fund…
                </h3>
                <div className="grid grid-cols-1 gap-3 tablet:grid-cols-3">
                  <div className="flex items-start gap-3 rounded-xl border border-border bg-bone p-4">
                    <div className="mt-px flex size-9 shrink-0 items-center justify-center rounded-full bg-green/12 text-green-darker">
                      <HomeIcon />
                    </div>
                    <div>
                      <p className="mb-1 text-body-xs text-neutral-darker">Luxury vacations per year ($10,000 each)</p>
                      <p className="text-body-sm font-bold text-gray-darker tabular-nums">
                        {annualSurplus >= 10_000
                          ? `${Math.floor(annualSurplus / 10_000)} per year`
                          : 'Grow your surplus'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 rounded-xl border border-border bg-bone p-4">
                    <div className="mt-px flex size-9 shrink-0 items-center justify-center rounded-full bg-green/12 text-green-darker">
                      <GraduationIcon />
                    </div>
                    <div>
                      <p className="mb-1 text-body-xs text-neutral-darker">Annual college savings added</p>
                      <p className="text-body-sm font-bold text-gray-darker tabular-nums">
                        {fmtFull(annualSurplus)} / yr
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 rounded-xl border border-border bg-bone p-4">
                    <div className="mt-px flex size-9 shrink-0 items-center justify-center rounded-full bg-green/12 text-green-darker">
                      <ForkIcon />
                    </div>
                    <div>
                      <p className="mb-1 text-body-xs text-neutral-darker">Nice dinners for two ($200 each)</p>
                      <p className="text-body-sm font-bold text-gray-darker tabular-nums">
                        {annualSurplus >= 200 ? `${Math.floor(annualSurplus / 200)} dinners` : 'Grow your surplus'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex items-start gap-4 rounded-xl border border-border bg-bone px-5 py-4">
                  <span className="mt-px shrink-0 text-body-lg" aria-hidden="true">
                    🏛️
                  </span>
                  <div>
                    <h4 className="mb-2 text-subtitle font-bold text-gray-darker">Don&apos;t forget Social Security</h4>
                    <p className="text-body-xs text-neutral-darker">
                      Your Coast FIRE projection doesn&apos;t include Social Security income, which can reduce how much
                      you may need from your savings. Benefits can begin as early as age 62, though waiting until 67 or
                      70 may increase your monthly payment.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Tips — always public for SEO crawl (not gated). */}
        <div className="mb-4">
          <p className="mb-2 text-body-xs font-bold text-green-darker uppercase">Things to watch for</p>
          <p className="text-title-xs mb-4 text-gray-darker">How to help protect your Coast FIRE plan</p>
          {TIPS.map((tip) => (
            <div key={tip.title} className="mb-3 rounded-2xl border border-border bg-card px-6 py-5">
              <p className="mb-1 text-body-sm font-bold text-black">{tip.title}</p>
              <p className="text-body-sm text-neutral-darker">{tip.body}</p>
            </div>
          ))}
        </div>

        {unlocked && isQualified && (
          <div className="dark mb-4 rounded-2xl bg-gray-darker px-8 py-7 text-center">
            <p className="mb-2 text-body-xs font-bold text-green-lighter-2 uppercase">NerdWallet Wealth Partners</p>
            <p className="text-title-sm text-white tablet:text-title-xs">
              What our calculator <br className="block tablet:hidden" />
              can&apos;t account for
            </p>
            <p className="mx-auto my-4 mb-5 max-w-130 text-body-sm text-bone">
              Your life, finances and priorities change. A NerdWallet Wealth Partners financial advisor can help you
              navigate it all, breaking down trade-offs and updating your financial plan as needed.
            </p>
            <LeadMagnetScheduleCTA segmentElement="Coast FIRE Ad Card" size="cta-sm" variant="funnel">
              Book a free intro call
            </LeadMagnetScheduleCTA>
          </div>
        )}

        {unlocked && (
          <p className="text-body-xs text-neutral-darker">
            Results are for illustrative purposes only and do not constitute financial advice.
          </p>
        )}
      </div>

      {/* Sticky banner — shown on unlock for $100K+ household income, all breakpoints */}
      {showStickyBanner && (
        <div className="dark fixed inset-x-0 bottom-0 z-100 bg-gray-darker px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-4px_20px_rgba(0,0,0,0.2)]">
          <div className="mx-auto flex max-w-225 items-center justify-center gap-4 tablet:gap-6">
            <p className="text-body-sm-bold text-white">
              Talk to a fiduciary advisor
              <span className="hidden text-body-sm-bold tablet:inline"> about what&apos;s next</span>
            </p>
            <ScheduleCallCTA
              segmentElement="Coast FIRE Sticky Banner"
              baseUrl={COAST_CALENDLY_HREF}
              variant="funnel"
              size="cta-sm"
              className="shrink-0 px-6"
            >
              Book a call
            </ScheduleCallCTA>
          </div>
        </div>
      )}
    </div>
  )
}
